import React, { Fragment, useReducer, useEffect } from "react";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import { useHistory, useParams } from "react-router-dom";
import ai from "../../helpers/axios";
import AirportAutocomplete from "../airports/airport-autocomplete";
import RegionAutocomplete from "../airports/region-autocomplete";

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      return {
        ...state,
        route: {
          ...state.route,
          [action.field]: action.value,
        },
      };
    }
    case "departureAirportChanged": {
      return {
        ...state,
        route: {
          ...state.route,
          departureAirport: {
            code: action.value.code,
            localCode: action.value.localCode || "",
            airportId: action.value.value,
          },
        },
        selectedDepartureAirport: action.value,
      };
    }
    case "arrivalAirportChanged": {
      return {
        ...state,
        route: {
          ...state.route,
          arrivalAirport: {
            code: action.value.code,
            localCode: action.value.localCode || "",
            airportId: action.value.value,
          },
        },
        selectedArrivalAirport: action.value,
      };
    }
    case "departureRegionChanged": {
      return {
        ...state,
        route: {
          ...state.route,
          departureRegion: action.value.value,
        },
        selectedDepartureRegion: action.value,
      };
    }
    case "arrivalRegionChanged": {
      return {
        ...state,
        route: {
          ...state.route,
          arrivalRegion: action.value.value,
        },
        selectedArrivalRegion: action.value,
      };
    }
    case "aircraftTypeToggled": {
      const updatedTypes = state.route.eligibleAircraftTypes.map((type) => {
        const typeId = type.aircraftTypeId._id || type.aircraftTypeId;
        if (typeId === action.typeId) {
          return { ...type, enabled: !type.enabled };
        }
        return type;
      });
      return {
        ...state,
        route: {
          ...state.route,
          eligibleAircraftTypes: updatedTypes,
        },
      };
    }
    case "dataBeganDownloading": {
      return {
        ...state,
        loading: true,
      };
    }
    case "dataDownloaded": {
      const data = action.payload.data;
      // Transform populated airport objects to autocomplete format
      const selectedDepAirport = data.departureAirport?.airportId ? {
        value: data.departureAirport.airportId._id,
        label: data.departureAirport.airportId.airportName || data.departureAirport.code,
        code: data.departureAirport.code,
        localCode: data.departureAirport.localCode || "",
      } : null;

      const selectedArrAirport = data.arrivalAirport?.airportId ? {
        value: data.arrivalAirport.airportId._id,
        label: data.arrivalAirport.airportId.airportName || data.arrivalAirport.code,
        code: data.arrivalAirport.code,
        localCode: data.arrivalAirport.localCode || "",
      } : null;

      // Transform populated region objects to autocomplete format
      const selectedDepRegion = data.departureRegion ? {
        value: data.departureRegion._id,
        label: data.departureRegion.name,
      } : null;

      const selectedArrRegion = data.arrivalRegion ? {
        value: data.arrivalRegion._id,
        label: data.arrivalRegion.name,
      } : null;

      return {
        ...state,
        route: data,
        selectedDepartureAirport: selectedDepAirport,
        selectedArrivalAirport: selectedArrAirport,
        selectedDepartureRegion: selectedDepRegion,
        selectedArrivalRegion: selectedArrRegion,
        loading: false,
      };
    }
    default:
      break;
  }
  return state;
};

const SingleClickToFlyContent = () => {
  const { id } = useParams();
  const history = useHistory();

  const initialState = {
    route: {
      departureAirport: {
        code: "",
        localCode: "",
        airportId: "",
      },
      arrivalAirport: {
        code: "",
        localCode: "",
        airportId: "",
      },
      departureRegion: "",
      arrivalRegion: "",
      eligibleAircraftTypes: [],
    },
    selectedDepartureAirport: null,
    selectedArrivalAirport: null,
    selectedDepartureRegion: null,
    selectedArrivalRegion: null,
    allAircraftTypes: [],
    loading: true,
  };

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [state, dispatch] = useReducer(reducer, initialState);

  const getData = async () => {
    dispatch({ type: "dataBeganDownloading" });

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let { data } = await ai.auth(token).get(`/api/clickToFly/${id}`);

    dispatch({
      type: "dataDownloaded",
      payload: {
        data,
      },
    });
  };

  useEffect(() => {
    getData();
    document.title = "Edit ClickToFly Route | evoJets HQ";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let formData = { ...state.route };

    await ai.auth(token).put(`/api/clickToFly/${id}`, formData);

    history.push("/click-to-fly/");

    setTimeout(() => {
      toast.success("ClickToFly Route Updated Successfully!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }, 50);
  };

  const handleDepartureAirportChange = (airport) => {
    if (airport) {
      dispatch({
        type: "departureAirportChanged",
        value: airport,
      });
    }
  };

  const handleArrivalAirportChange = (airport) => {
    if (airport) {
      dispatch({
        type: "arrivalAirportChanged",
        value: airport,
      });
    }
  };

  const handleDepartureRegionChange = (region) => {
    if (region) {
      dispatch({
        type: "departureRegionChanged",
        value: region,
      });
    }
  };

  const handleArrivalRegionChange = (region) => {
    if (region) {
      dispatch({
        type: "arrivalRegionChanged",
        value: region,
      });
    }
  };

  const handleAircraftTypeToggle = (typeId) => {
    dispatch({
      type: "aircraftTypeToggled",
      typeId: typeId,
    });
  };

  if (state.loading) {
    return <div>Loading...</div>;
  }

  return (
    <Fragment>
      <h2 id="editClickToFlyRouteHeading">Edit ClickToFly Route</h2>

      <form id="editClickToFlyRoute" onSubmit={handleSubmit}>
        <AirportAutocomplete
          selectedAirport={state.selectedDepartureAirport}
          handleAirportChange={handleDepartureAirportChange}
          label="Departure Airport"
        />

        <AirportAutocomplete
          selectedAirport={state.selectedArrivalAirport}
          handleAirportChange={handleArrivalAirportChange}
          label="Arrival Airport"
        />

        <RegionAutocomplete
          selectedRegion={state.selectedDepartureRegion}
          handleRegionChange={handleDepartureRegionChange}
          label="Departure Region"
        />

        <RegionAutocomplete
          selectedRegion={state.selectedArrivalRegion}
          handleRegionChange={handleArrivalRegionChange}
          label="Arrival Region"
        />

        <div className="form-group">
          <label htmlFor="eligibleAircraftTypes">Eligible Aircraft Types</label>
          <div className="aircraft-types-list">
            {state.route.eligibleAircraftTypes && state.route.eligibleAircraftTypes.map((type) => (
              <div key={type.aircraftTypeId._id || type.aircraftTypeId} className="aircraft-type-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={type.enabled}
                    onChange={() => handleAircraftTypeToggle(type.aircraftTypeId._id || type.aircraftTypeId)}
                  />
                  {" "}{type.name}
                </label>
              </div>
            ))}
          </div>
        </div>

        <input
          type="submit"
          value="Update Route"
          className="evo-blue base-button"
        />
      </form>
    </Fragment>
  );
};

export default SingleClickToFlyContent;

